import { telemetryUrlOf } from './app/mode';
import { AudioBus, createBrowserAudio } from './audio/synth';
import { type BootConfig, randomSessionSeed, readBootConfig } from './config';
import {
  PACK_LABEL,
  type PackListing,
  browserPackSource,
  bundledPack,
  listPacks,
  loadBootPack,
} from './content';
import {
  ENGINE_VERSION,
  GRID_VERSION,
  angerStage,
  isOver,
  localeOf,
  packHash,
  packKindOf,
  rankKeyOf,
  revealHints,
  rulesForLocale,
  rulesHash,
  toTileViews,
} from './engine';
import { currentLocale, setLocale, t } from './i18n';
import { mountKeyboard } from './input/keyboard';
import { mountPointer } from './input/pointer';
import { webPlatform } from './platform';
import { startLoop } from './runtime/loop';
import { type Session, createSession } from './runtime/session';
import { askPreRun, readKnown } from './selfreport/pre-run';
import { cardInputOf, cardText, describeOpponent, duelOutcome } from './share/card';
import { attemptsLeft, consumeAttempt, duelKey, localDuelStore } from './share/duel';
import {
  type ChallengeLink,
  buildChallengeLink,
  isSameVersion,
  sanitizeCode,
  versionTag,
} from './share/link';
import { browserShareHost, shareText } from './share/share';
import { type LocalStats, commitRun, describeBest, readStats, writeStats } from './store/local';
import { Telemetry, probeEnv, readIdentity } from './telemetry';
import { browserUgcFetch, createUgcClient } from './ugc/client';
import {
  augmentGameOver,
  queryDom,
  renderBand,
  renderHud,
  renderRevive,
  renderStamps,
  showGameOver,
  showIntro,
  showManualShare,
  showToast,
  updateRevive,
} from './view/dom';
import { loadGameFont } from './view/font';
import { applyLayout, computeLayout, probeViewport } from './view/layout';
import { Renderer } from './view/renderer';
import { createScreens } from './view/screen';
import { showTitle } from './view/title';
import { mountStarRow, renderUgcSubmit, renderUgcTop } from './view/ugc';

const FONT_FAMILY = 'AjaeGlyph';

async function boot(): Promise<void> {
  // UI 로케일 — 빌드 상수가 기본, `?lang=en` 은 개발용(RULES §1.17)
  const langParam = new URL(location.href).searchParams.get('lang');
  if (langParam === 'en' || langParam === 'ko') setLocale(langParam);
  // 로딩 표시 — 폰트·팩을 기다리는 동안 검은 화면만 남지 않게. #stage 는 첫 레이아웃까지 그대로 숨긴다(SPEC §11.6)
  const bootIndicator = document.createElement('div');
  bootIndicator.id = 'boot';
  bootIndicator.setAttribute('role', 'status');
  bootIndicator.textContent = t().note.loading;
  document.body.append(bootIndicator);
  const cfg0 = readBootConfig(
    location.href,
    randomSessionSeed(),
    import.meta.env.VITE_APP_MODE as string | undefined,
    undefined,
    (import.meta.env.VITE_DEFAULT_PACK as string | undefined) ?? 'core',
  );
  const dom = queryDom();
  const root = document.documentElement;
  const stage = document.getElementById('stage') as HTMLElement;
  const canvas = document.getElementById('field') as HTMLCanvasElement;
  const screens = createScreens(dom.overlay);

  // 폰트 · 팩 · 팩 목록을 **동시에** 기다린다 — 서비스가 응답하지 않을 때의 대기가 타임아웃의 합(1.5 + 2.5 + 2.5초)이
  // 아니라 가장 긴 하나다(SPEC §11.6).
  // 팩 로더(ADR-046) — 링크가 못 박은 버전 → 최신 → 캐시 → 번들. 못 받으면 도전장을 버리고 새 판으로
  const packSrc = browserPackSource((import.meta.env.VITE_PACK_ORIGIN as string | undefined) ?? '');
  // 목록은 팩 로케일을 따른다 — 번들에 있는 팩이면 그 로케일로 미리 출발시키고, 받은 팩과 어긋날 때만 다시 받는다
  const hinted = bundledPack(cfg0.packId);
  const listLocale = hinted !== null ? localeOf(hinted) : currentLocale();
  const [fontStatus, booted, earlyList] = await Promise.all([
    loadGameFont(FONT_FAMILY, cfg0.viewRules.fontLoadTimeoutMs),
    loadBootPack(cfg0.packId, cfg0.packPinned, packSrc),
    listPacks(listLocale, packSrc),
  ]);
  // 도전장이 정한 팩을 받지 못했다 → 도전장을 버리고 새 판 · 타이틀에 안내(challengeLine 없이도 그린다 · title.ts).
  // 도전장 없이 번들 밖 팩(?pack=)이 core 로 떨어진 경우는 "도전장의 팩…" 문구가 맞지 않아 띄우지 않는다 — 팩 줄이 core 를 보인다
  const packLoadNote =
    booted.unavailable && cfg0.challenge !== null ? t().note.packUnavailable : null;
  const pack = booted.loaded.pack;
  // 규칙은 팩 로케일의 정답 길이 프로필을 따른다(RULES §1.17). ko 는 해시가 변하지 않는다
  const engineRules = rulesForLocale(localeOf(pack), cfg0.engineRules);
  const cfg: BootConfig = {
    ...cfg0,
    engineRules,
    engineRulesHash: rulesHash(engineRules),
    challenge: booted.unavailable ? null : cfg0.challenge,
  };
  const packList: readonly PackListing[] =
    localeOf(pack) === listLocale ? earlyList : await listPacks(localeOf(pack), packSrc);
  const packLabelOf = (id: string): string =>
    packList.find((p) => p.id === id)?.label ?? PACK_LABEL[id] ?? id;

  let probe = probeViewport(cfg.viewRules);
  let metrics = computeLayout(probe, cfg.viewRules);
  applyLayout(root, canvas, metrics);
  // style.css 가 #stage 를 visibility:hidden 으로 시작한다 — 첫 레이아웃 전 한 프레임(기본 300×150 캔버스·빈 띠)이
  // 그려지는 FOUC 차단(SPEC §11.6). 여기서 처음 푼다. 부팅이 그 전에 실패하면 오류 패널(#overlay, stage 밖)만 보인다.
  stage.style.visibility = 'visible';
  bootIndicator.remove();
  const renderer = new Renderer(canvas, metrics, FONT_FAMILY);
  renderer.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── 레이아웃 · 회전 ────────────────────────────────────────────────────
  // 타이틀 · 자가표기(사용자를 기다리는 await)보다 **먼저** 건다 — 그 사이의 뷰포트 변화(카톡 인앱의 첫 프레임 흔들림)를
  // 놓치면 필드가 첫 측정 크기(25px 까지)로 고착된다. ResizeObserver 는 resize 이벤트를 놓치는 환경의 안전망이다(SPEC §11.7).
  let session: Session | null = null; // 타이틀이 떠 있는 동안은 null — 루프 · 레이아웃 핸들러가 전부 가드한다
  let landscape = false;
  let rotateDismissed = false; // 회전 안내 [계속하기] — 이 페이지 세션 동안 다시 띄우지 않는다(RULES §2.7)
  let resumeAt = 0;
  let relayout = 0;
  const relayoutNow = (): void => {
    probe = probeViewport(cfg.viewRules);
    const nowLandscape = probe.orientation === 'landscape' && probe.isTouch && !rotateDismissed;
    // 회전 오버레이는 캔버스를 재생성하지 않는다 — 세로 레이아웃을 그대로 보존하고 루프를 정지한다
    root.classList.toggle('landscape', nowLandscape);
    if (landscape && !nowLandscape && session !== null) {
      // 세로 복귀 · [계속하기] — 3틱 유예 뒤 재개 (엔진 상태를 건드리지 않으므로 결정론 무손상)
      resumeAt = performance.now() + 3 * session.effectiveTickMs();
    }
    landscape = nowLandscape;
    if (nowLandscape) return;
    const next = computeLayout(probe, cfg.viewRules);
    const same =
      next.cell === metrics.cell &&
      next.canvasDeviceW === metrics.canvasDeviceW &&
      next.canvasDeviceH === metrics.canvasDeviceH;
    metrics = next;
    applyLayout(root, canvas, metrics);
    if (!same) renderer.resize(metrics); // 출력이 같으면 정적 레이어 재생성은 건너뛴다
  };
  const onResize = (): void => {
    window.clearTimeout(relayout);
    relayout = window.setTimeout(relayoutNow, 60);
  };
  window.addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('resize', onResize);
  if (typeof ResizeObserver === 'function') new ResizeObserver(onResize).observe(root);
  // 회전 안내의 [계속하기] — #rotate 는 index.html 에 있고 버튼만 여기서 붙인다
  const rotateEl = document.getElementById('rotate');
  if (rotateEl !== null) {
    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'rotate-continue';
    keep.textContent = t().rotate.continue;
    // 뮤트 버튼과 같다 — 스테이지 스와이프 추적으로 새지 않게 세 이벤트를 모두 막는다
    for (const ev of ['pointerdown', 'pointerup', 'pointercancel'] as const) {
      keep.addEventListener(ev, (e) => e.stopPropagation());
    }
    keep.addEventListener('click', () => {
      rotateDismissed = true;
      window.clearTimeout(relayout);
      relayoutNow();
    });
    rotateEl.append(keep);
  }

  // 사운드 — 언락은 사용자 제스처 핸들러 안에서 동기로. 타이틀 [시작]이 1순위, 첫 터치/키가 보조다.
  const audio = new AudioBus(createBrowserAudio);
  const unlock = (): void => audio.unlock();
  for (const ev of ['pointerdown', 'pointerup', 'touchend', 'keydown'] as const) {
    window.addEventListener(ev, unlock, { passive: true });
  }
  const muteBtn = document.createElement('button');
  muteBtn.id = 'mute';
  muteBtn.type = 'button';
  muteBtn.style.cssText =
    'position:fixed;right:10px;bottom:calc(var(--hud-h) + 12px);z-index:5;min-width:44px;height:32px;' +
    'font:inherit;font-size:15px;color:var(--ink-dim);background:var(--ui-bg);border:2px solid var(--ui-line);' +
    'cursor:pointer;-webkit-tap-highlight-color:transparent;';
  const syncMute = (): void => {
    muteBtn.textContent = audio.isMuted() ? '♪x' : '♪';
    muteBtn.setAttribute('aria-pressed', String(audio.isMuted()));
    muteBtn.setAttribute('aria-label', audio.isMuted() ? t().mute.on : t().mute.off);
  };
  syncMute();
  // pointerup 만 막으면 마우스에서 trackId 가 해제되지 않아 이후 모든 pointermove 가 방향을 커밋한다
  for (const ev of ['pointerdown', 'pointerup', 'pointercancel'] as const) {
    muteBtn.addEventListener(ev, (e) => e.stopPropagation());
  }
  muteBtn.addEventListener('click', () => {
    audio.setMuted(!audio.isMuted());
    if (!audio.isMuted()) {
      audio.unlock();
      audio.play('ui_tap');
    }
    syncMute();
  });
  document.body.append(muteBtn);

  const id = readIdentity();
  // UGC(ADR-048) — 같은 오리진(앱은 VITE_PACK_ORIGIN)의 /ugc. uid 는 계측 uid 를 영숫자로 다듬은 것
  const ugc = createUgcClient(packSrc.origin, browserUgcFetch());
  const ugcUid = ((): string => {
    const base = id.uid.replace(/[^A-Za-z0-9_-]/g, '');
    return (base.length >= 4 ? base : `${base}xxxx`).slice(0, 64);
  })();
  const weeklyListed = packList.some((p) => p.id === 'ugc-weekly');
  const goPack = (packId: string): void => {
    const u = new URL(location.href);
    u.searchParams.set('pack', packId);
    for (const k of ['r', 'v', 'sd', 'w', 'lt', 'x', 'i', 'c', 'pk']) u.searchParams.delete(k);
    location.assign(u.toString());
  };
  const env = probeEnv(probe.vpW, probe.vpH);
  // 수집처는 연구 빌드에서만, 값이 있을 때만(mode.ts telemetryUrlOf) — 제품 빌드는 어떤 .env 로도 플레이 기록을 보내지 않는다
  const tlmUrl = telemetryUrlOf(cfg.mode, import.meta.env.VITE_TLM_URL as string | undefined);
  if (cfg.flags.telemetryBanner && tlmUrl === null) {
    // 이 배너가 보이는 빌드로 G0 링크를 뿌리면 수집이 0건으로 끝난다
    const b = document.createElement('div');
    b.id = 'tlm-warn';
    b.textContent = t().note.devBuild;
    document.body.append(b);
  }
  const tlm = new Telemetry({
    url: tlmUrl,
    uid: id.uid,
    sid: randomSessionSeed(),
    batchSize: 20,
  });

  tlm.track(
    'session_start',
    {
      variant: cfg.variant, // 'default' | 'no-decoy' — DECOY_ON=false 재측정 빌드 층화(RULES §16 #8)
      p: cfg.participant,
      os: env.os,
      osv: env.osv,
      br: env.br,
      iab: env.iab,
      scw: env.scw,
      sch: env.sch,
      vw: Math.round(probe.vpW),
      vh: Math.round(probe.vpH),
      rawInnerH: window.innerHeight,
      rawVvH: window.visualViewport?.height ?? -1,
      ori: probe.orientation,
      dpr: probe.dpr,
      ptr: env.ptr,
      tp: env.tp,
      dsk: env.dsk,
      cellCss: Math.round((metrics.cell / probe.dpr) * 10) / 10,
      glyphCss: Math.round((metrics.glyphDevice / probe.dpr) * 10) / 10,
      chromeTier: metrics.tier,
      degraded: metrics.degraded,
      tooSmall: metrics.tooSmall,
      bottomGuard: probe.bottomGuard,
      fontStatus: fontStatus === 'loaded' ? 'loaded' : 'fallback',
      preset: cfg.preset,
      rulesOverridden: cfg.rulesOverridden,
      engineRulesHash: cfg.engineRulesHash,
      viewRulesHash: cfg.viewRulesHash,
      packVersion: pack.version,
      engineVersion: ENGINE_VERSION,
      gridVersion: GRID_VERSION,
      buildSha: __BUILD_SHA__,
      us: id.us,
      nw: id.nw,
      pv: id.pv,
      lv: id.lv,
      tz: env.tz,
      ref: env.ref,
      dbg: cfg.debug,
      mode: cfg.mode,
      ovApplied: cfg.report.applied,
      ovRejected: cfg.report.rejected,
    },
    true,
  );

  // 사전 자가표기 — 연구 모드에서만, 첫 세션 최초 1회. 정답은 절대 노출하지 않는다.
  // 제품 모드에서는 24문항 리스트가 팩 전량을 미리 읽히는 스포일러이자 이탈 지점이라 띄우지 않는다.
  const known = cfg.flags.preRunSurvey ? await askPreRun(pack, dom.overlay) : readKnown();

  // ── 판 수명주기 ────────────────────────────────────────────────────────
  let stats: LocalStats = readStats(pack.id);
  // 기기·팩 기준 첫 방문 — READY 인트로 3줄은 이때만 띄운다. 재방문자에게는 READY pip 이 전부다(ADR-037)
  const firstVisit = stats.runs === 0;
  let runIndex = cfg.runIndex;
  let cfgRun: BootConfig = cfg; // 도전장을 버리면 새 세션 시드로 바뀐다
  // ── 링크 대결 (ADR-028) — 웹 한정 모드: 도전 판 + 재도전 1회 · 승패는 링크의 기록을 기기에서 비교
  const duelStore = localDuelStore();
  let duel: {
    readonly link: ChallengeLink;
    readonly key: string;
    readonly versionOk: boolean;
  } | null =
    cfg.challenge !== null
      ? {
          link: cfg.challenge,
          key: duelKey(cfg.challenge.sessionSeed, cfg.challenge.runIndex),
          versionOk: isSameVersion(cfg.challenge, pack.version, pack.id),
        }
      : null;
  const origin = `${location.origin}${location.pathname}`;
  const packIdx = packList.findIndex((p) => p.id === pack.id);
  const nextPack = packList.length > 1 ? packList[(packIdx + 1) % packList.length] : undefined;
  const inviter = sanitizeCode(id.uid.replace(/[^A-Za-z0-9]/g, '').slice(0, 6));
  // 앱 CTA 주소(.env.example · RULES §1.15) — 비면 CTA 를 아예 띄우지 않는다
  const appUrl = import.meta.env.VITE_APP_URL ?? '';
  const platform = webPlatform(); // P4: Capacitor 구현체로 교체. 웹은 전부 no-op
  void platform;
  const opponentLine = (): string | null =>
    duel?.versionOk
      ? describeOpponent(
          duel.link.record,
          cfg.engineRules.runWordCount,
          cfg.engineRules.nominalTickMs,
        )
      : null;
  function leaveDuel(): void {
    duel = null;
    cfgRun = { ...cfg, seed: randomSessionSeed(), challenge: null, runIndex: 0 };
    runIndex = 0;
  }
  let gameIndex = 0;
  let dismissIntro: (() => void) | null = null;
  let lastGameOverAt = -1;
  let overSession: Session | null = null; // 게임오버 래치는 오버레이 클래스가 아니라 세션 동일성으로
  let panelShownAt = -1;
  let lastNewBest = false;

  function newSession(): Session {
    gameIndex += 1;
    return createSession({ cfg: cfgRun, pack, tlm, known, runIndex, gameIndex });
  }

  function startRun(): void {
    audio.droneOff(); // 이전 판의 페널티 드론이 새 판으로 새지 않게
    panelShownAt = -1;
    overSession = null;
    lastNewBest = false;
    if (duel !== null) consumeAttempt(duelStore, duel.key); // 웹: 도전 판 + 1회
    session = newSession();
    screens.close();
    stage.classList.remove('idle');
    tlm.track('game_start', {
      variant: cfg.variant, // 'default' | 'no-decoy' — DECOY_ON=false 재측정 빌드 층화(RULES §16 #8)
      gi: gameIndex,
      sessionSeed: cfgRun.seed,
      runIndex,
      seed: session.runner.state.seed,
      packVersion: pack.version,
      packHash: packHash(pack), // 재현 키 6요소 중 하나 — session_start 는 팩 로드 전이라 여기 싣는다
      engineRulesHash: cfg.engineRulesHash,
      preset: cfg.preset,
      rulesOverridden: cfg.rulesOverridden,
      sre: lastGameOverAt < 0 ? -1 : Math.round(performance.now() - lastGameOverAt),
      vw: Math.round(probe.vpW),
      vh: Math.round(probe.vpH),
    });
    if (gameIndex === 1 && firstVisit) {
      // READY 11틱에 묶는다 — draw 루프가 readyTicksLeft === 0 인 첫 프레임에 지운다. 15초는 안전망.
      dismissIntro = showIntro(dom, t().intro, 15000);
    }
  }

  function restart(src: 'gameover' | 'key'): void {
    audio.play('ui_tap');
    tlm.track('restart_click', {
      gi: gameIndex,
      src,
      since: lastGameOverAt < 0 ? -1 : Math.round(performance.now() - lastGameOverAt),
    });
    if (duel !== null) {
      // 같은 판 다시(재도전 1회) — 소진했으면 도전장을 버리고 새 판
      if (attemptsLeft(duelStore, duel.key) === 0) leaveDuel();
    } else runIndex += 1;
    startRun();
  }

  // 타이틀 → [시작](동기 오디오 언락) → 첫 판
  stage.classList.add('idle');
  const exhaustedAtBoot = duel !== null && attemptsLeft(duelStore, duel.key) === 0;
  await showTitle(
    screens,
    {
      stats,
      runWordCount: cfg.engineRules.runWordCount,
      challengeLine:
        duel === null
          ? null
          : duel.versionOk
            ? describeOpponent(
                duel.link.record,
                cfg.engineRules.runWordCount,
                cfg.engineRules.nominalTickMs,
              )
            : t().note.notSame,
      challengeNote:
        duel === null
          ? packLoadNote
          : !duel.versionOk
            ? t().note.versionOld
            : exhaustedAtBoot
              ? t().note.webTwice
              : null,
      onDecline: duel === null ? null : leaveDuel,
      packLine: t().title.packLine(packLabelOf(pack.id), packKindOf(pack) === 'inference'),
      // 연구 빌드는 회차의 팩이 고정이라 전환 버튼을 숨긴다 — 한 회차의 기록에 두 팩이 섞이지 않게
      onSwitchPack:
        cfg.mode === 'product' && nextPack !== undefined
          ? {
              label: t().title.switchPack(nextPack.label),
              go: () => {
                const u = new URL(location.href);
                u.searchParams.set('pack', nextPack.id);
                for (const k of ['r', 'v', 'sd', 'w', 'lt', 'x', 'i', 'c', 'pk'])
                  u.searchParams.delete(k);
                location.assign(u.toString());
              },
            }
          : null,
      extras:
        cfg.mode === 'product' && duel === null
          ? [
              {
                label: t().ugc.submitBtn,
                open: (root, back) =>
                  renderUgcSubmit(
                    root,
                    {
                      client: ugc,
                      uid: ugcUid,
                      locale: localeOf(pack),
                      toast: (x) => showToast(dom, x, 1800),
                      playWeekly: null,
                    },
                    back,
                  ),
              },
              {
                label: t().ugc.topBtn,
                open: (root, back) =>
                  renderUgcTop(
                    root,
                    {
                      client: ugc,
                      uid: ugcUid,
                      locale: localeOf(pack),
                      toast: (x) => showToast(dom, x, 1800),
                      playWeekly:
                        weeklyListed && pack.id !== 'ugc-weekly'
                          ? () => goPack('ugc-weekly')
                          : null,
                    },
                    back,
                  ),
              },
            ]
          : [],
    },
    () => {
      if (exhaustedAtBoot) leaveDuel();
      audio.unlock();
      audio.play('ui_tap');
    },
  );
  startRun();
  tlm.install(() => session?.forceEnd('quit'));

  // ── 입력 ─────────────────────────────────────────────────────────────
  const onDir = (d: Parameters<Session['pushInput']>[0]): void => {
    if (session === null) return;
    dismissIntro?.();
    dismissIntro = null;
    session.pushInput(d);
  };
  mountPointer(document.body, cfg.viewRules, { onDir });
  mountKeyboard(onDir);
  window.addEventListener('keydown', (e) => {
    // 부활 화면: 숫자 1~4 로 선택 (데스크톱 확인용 · 폰은 버튼)
    if (screens.current() === 'revive' && session !== null) {
      const m = /^Digit([1-4])$/.exec(e.code);
      if (m !== null) {
        e.preventDefault();
        session.pickChoice(Number(m[1]) - 1);
      }
      return;
    }
    // 게임오버 패널에서 Enter/Space 재시작 — 입력 잠금이 풀린 뒤에만
    if (screens.current() !== 'gameover') return;
    if (e.code !== 'Enter' && e.code !== 'Space') return;
    if (document.activeElement instanceof HTMLTextAreaElement) return;
    e.preventDefault();
    restart('key');
  });

  // 일시정지(탭 숨김·가로 회전·재개 카운트다운)의 시작 시각은 루프(loop.ts)가 한 시계로 관리한다 —
  // 여기서 따로 재면 가로 회전 30분이 abandon 판정에서 빠진다. 누적은 아래 onPauseEnd 로 받는다.

  // ── 디버그 표면 (RULES §1.13 DEBUG_SURFACE) — ?debug=1 에서만, 읽기 전용, 타일은 kind·slot 을 뺀다(ADR-008)
  if (cfg.debug) {
    Object.defineProperty(window, '__ajae', {
      configurable: true,
      get: () => {
        if (session === null) return null;
        const s = session.runner.state;
        return {
          tick: s.tick,
          phase: s.phase,
          endReason: s.endReason,
          readyTicksLeft: s.readyTicksLeft,
          clearTicksLeft: s.clearTicksLeft,
          snake: { cells: [...s.snake.cells], dir: s.snake.dir, pending: s.snake.pending },
          tiles: toTileViews(s),
          word: { index: s.word.index, eaten: s.word.eaten, answerLen: s.word.answer.length },
          question: session.question().q,
          penalty: s.penalty.status,
          growTicks: s.growTicks,
          growPauseLeft: s.growPauseLeft,
          peakLength: s.peakLength,
          ledger: s.ledger,
          revive: session.reviveView(), // 판정 전에는 정답 위치가 없다
          records: s.records.map((r) => ({
            index: r.index,
            outcome: r.outcome,
            endTick: r.endTick,
          })),
        };
      },
    });
  }

  // ── 루프 ─────────────────────────────────────────────────────────────
  startLoop(cfg.viewRules, {
    tickMs: () => session?.effectiveTickMs() ?? cfg.engineRules.tickMsTable[0] ?? 180,
    advance: () => session?.advance(),
    done: () => session === null || isOver(session.runner.state),
    // 정지 한 구간이 끝날 때마다 — game_over 의 hiddenMs / hiddenCount (RULES §2.6 '일시정지 누적')
    onPauseEnd: (ms) => {
      if (session === null) return;
      session.hiddenMs += ms;
      session.hiddenCount += 1;
    },
    // 연속 정지 > pauseAbandonMs 로 복귀 — 탭 숨김이든 가로 회전이든 같은 규칙(RULES §2.6 · UPLIFT §17.4 ROTATE_PAUSE)
    onPauseTooLong: () => session?.forceEnd('abandoned'),
    // 가로 회전·세로 복귀 3틱 카운트다운. 시작 시각은 여기서 재지 않는다 — 루프의 정지 시계가 hidden 과 합친다
    paused: () => landscape || performance.now() < resumeAt,
    alphaSteps: () =>
      session !== null && session.runner.state.penalty.status === 2
        ? cfg.viewRules.alphaStepsPenalty
        : cfg.viewRules.alphaSteps,
    draw: (alpha) => {
      if (session === null) return;
      const s = session.runner.state;
      const seal = session.seal();
      // 부활 화면 — 페이즈가 revive 인 동안만 열고, 나가면 닫는다
      if (s.phase === 'revive') {
        const rv = session.reviveView();
        if (rv !== null) {
          if (screens.current() !== 'revive') {
            const captured = session;
            screens.open('revive', (root) =>
              renderRevive(root, rv, (i) => {
                if (session === captured) captured.pickChoice(i);
              }),
            );
          }
          updateRevive(dom.overlay, rv);
        }
      } else if (screens.current() === 'revive') {
        screens.close('revive');
      }
      if (dismissIntro !== null && s.readyTicksLeft === 0) {
        dismissIntro();
        dismissIntro = null;
      }

      // 큐 → 사운드 (이펙트와 같은 버스라 캐치업 프레임에서도 화면과 어긋나지 않는다)
      let badThisFrame = false;
      for (const c of session.cues) {
        switch (c.k) {
          case 'eat_good':
            audio.play('eat_correct', { combo: c.combo });
            break;
          case 'eat_bad':
            badThisFrame = true;
            audio.play(c.atFloor ? 'eat_wrong_floor' : 'eat_wrong');
            showToast(dom, t().toast.wrong, 1000);
            break;
          case 'ready':
            audio.play('countdown_beep');
            break;
          case 'go':
            audio.play('countdown_go');
            break;
          case 'word_end':
            if (c.outcome === 'solved') audio.play(c.perfect ? 'word_perfect' : 'word_solved');
            else if (c.outcome !== 'died') audio.play('word_failed');
            break;
          case 'relocate':
            audio.play('relocate');
            break;
          case 'penalty_on':
            // 같은 틱의 오답음(200Hz 이하)이 마스킹하므로 억제한다 — 실측 63% 동시 발생
            if (!badThisFrame) audio.play('penalty_on');
            audio.droneOn();
            break;
          case 'penalty_off':
            audio.play('penalty_off');
            audio.droneOff();
            break;
          case 'over':
            audio.droneOff();
            if (c.reason === 'clear') audio.play('run_clear');
            else if (c.reason === 'wall' || c.reason === 'self') audio.play('death');
            break;
          case 'revive_open':
            audio.droneOff();
            audio.play('death');
            break;
          case 'revive_judge':
            audio.play(c.result === 'correct' ? 'revive_ok' : 'revive_fail');
            break;
          case 'revive_done':
            break;
          default:
            break;
        }
      }
      session.cues.length = 0;

      // 사망 경과 ms 는 뷰 전용이다 — 엔진에 들어가지 않으므로 결정론에 닿지 않는다
      const deadMs = isOver(s) && panelShownAt >= 0 ? performance.now() - panelShownAt : -1;
      // 분노 게이지 — wordTick 의 순수 함수. 붉어지고(렌더러) 빨라지고(tickMs) 힌트가 열린다(띠)
      const stage = angerStage(s.word.wordTick, cfg.engineRules);
      const hint = s.phase === 'play' ? revealHints(session.question(), stage) : null;
      renderer.draw({
        prev: session.prev,
        cur: s,
        alpha,
        effects: session.effects,
        shake: session.shake,
        seal,
        deadMs: s.endReason === 'clear' ? -1 : deadMs,
        anger: stage,
      });
      const readyPip =
        s.readyTicksLeft > 0 && session.readyTotal > 0
          ? Math.ceil((s.readyTicksLeft / session.readyTotal) * 3)
          : 0;
      renderBand(dom, s, session.question().q, readyPip, hint);
      renderStamps(dom, seal);
      renderHud(
        dom,
        s,
        seal,
        opponentLine()?.replace(t().opponent.rival, t().opponent.goal) ?? null,
      );

      // 게임오버 래치 — 판당 정확히 1회. 기록 커밋도 여기서만.
      if (isOver(s) && overSession !== session) {
        overSession = session;
        panelShownAt = performance.now();
        lastGameOverAt = panelShownAt;
        session.effects = [];
        session.shake = 0;
        if (s.endReason !== 'quit' && s.endReason !== 'abandoned') {
          const c = commitRun(stats, session.runner.result());
          stats = c.stats;
          lastNewBest = c.newBest;
          writeStats(stats, pack.id);
        }
        tlm.flush();
      }
      // 400ms 입력 잠금 뒤에야 패널을 띄운다. 자동 재시작은 하지 않는다.
      if (
        overSession === session &&
        panelShownAt >= 0 &&
        screens.current() === null &&
        performance.now() - panelShownAt >= cfg.viewRules.gameoverInputLockMs
      ) {
        const captured = session;
        screens.open('gameover', () => {
          showGameOver(dom, s, seal, () => {
            if (session !== captured) return;
            restart('gameover');
          });
          const panel = dom.overlay.querySelector('.panel');
          if (panel !== null && stats.best !== null) {
            const line = document.createElement('p');
            line.className = lastNewBest ? 'best-line new' : 'best-line';
            line.textContent = lastNewBest
              ? t().over.newRecord(describeBest(stats.best, cfg.engineRules.runWordCount))
              : t().over.best(describeBest(stats.best, cfg.engineRules.runWordCount));
            const btn = panel.querySelector('button');
            if (btn !== null) panel.insertBefore(line, btn);
            else panel.append(line);
          }
          // 결과 카드 · 공유 · 대결 (ADR-028)
          const res = captured.runner.result();
          const theirs = duel?.versionOk ? duel.link.record : null;
          const outcome =
            theirs === null
              ? null
              : duelOutcome(rankKeyOf(res), {
                  ...theirs,
                  endReason: theirs.sealed ? 'clear' : 'wall',
                });
          const url = buildChallengeLink(origin, {
            sessionSeed: cfgRun.seed,
            runIndex,
            version: versionTag(pack.version),
            pack: pack.id,
            record: {
              sealed: res.sealed,
              solved: res.solved,
              lastSolveTick: res.lastSolveTick,
              reviveUsed: res.reviveUsed,
            },
            inviter,
            channel: 'kakao',
          });
          const text = cardText(
            cardInputOf(res, {
              runWordCount: cfg.engineRules.runWordCount,
              nominalTickMs: cfg.engineRules.nominalTickMs,
              gameIndex,
              hiddenMs: Math.round(captured.hiddenMs),
              personalBest: lastNewBest,
              duel: outcome,
              url,
            }),
          );
          // 카드 텍스트는 버튼 핸들러 진입 전에 만들어 둔다 — navigator.share 는 제스처 안에서 동기 호출돼야 한다
          const share = async (): Promise<void> => {
            const r = await shareText(text, browserShareHost());
            if (r.outcome === 'shared') showToast(dom, t().toast.shared, 1200);
            else if (r.outcome === 'copied') showToast(dom, t().toast.copied, 1600);
            else if (r.outcome === 'manual') showManualShare(dom, text);
          };
          const left = duel !== null ? attemptsLeft(duelStore, duel.key) : 0;
          const oppo = opponentLine();
          augmentGameOver(dom, {
            duelLine: outcome === null || oppo === null ? null : t().over.duel(outcome, oppo),
            restartLabel: duel === null ? null : left > 0 ? t().over.sameAgain : t().over.newRun,
            onShare: share,
            onCounter: duel !== null ? share : null,
            // 앱 CTA 는 주소가 있을 때만 — 비면 링크 없는 회색 문단이 되므로 띄우지 않는다
            appCta:
              duel !== null && left === 0 && appUrl !== ''
                ? { label: t().over.appCta, href: appUrl }
                : null,
          });
          // 별 주기(ADR-048) — 이 판에 나온 문제에만. 서비스가 발행본 슬롯 → 풀 문제로 푼다
          if (cfg.mode === 'product') {
            const panelEl = dom.overlay.querySelector('.panel');
            if (panelEl !== null) {
              const items = res.records.map((w) => ({
                slot: w.questionId,
                q: pack.questions.find((q) => q.id === w.questionId)?.q ?? w.questionId,
              }));
              mountStarRow(panelEl, items, (slot, on) =>
                ugc.vote(ugcUid, pack.id, pack.version, slot, on).then((r) => r.votes),
              );
            }
          }
        });
      }
    },
  });
}

boot().catch((err: unknown) => {
  document.getElementById('boot')?.remove(); // 로딩 표시가 오류 패널 뒤에 남지 않게
  const el = document.getElementById('overlay');
  if (el !== null) {
    el.className = 'on';
    el.innerHTML = `<div class="panel"><h1>${t().crash.title}</h1><p>${t().crash.body}</p><button type="button" onclick="location.reload()">${t().crash.reload}</button><details style="margin-top:12px;text-align:left;font-size:11px;color:#a29cc4"><summary>${t().crash.details}</summary><pre style="white-space:pre-wrap">${String(err).replace(/</g, '&lt;')}</pre></details></div>`;
  }
  console.error(err);
});
